import Quill from "quill";
import React from "react";
import { createPortal } from "react-dom";

import { generateId } from "base/utils";

import { FORMAT_TYPES } from "../index.js";
import { Scroll } from "./scroll.js";

const BlockEmbed = Quill.import("blots/block/embed");
const Embed = Quill.import("blots/embed");

const CUSTOM_INLINE = [
  FORMAT_TYPES.MENTION,
  FORMAT_TYPES.HASHTAG,
  FORMAT_TYPES.FIELD,
  FORMAT_TYPES.PROPERTY
];

export const CustomBlock = (type, blotComponent, flowId) => {
  const EmbedType = CUSTOM_INLINE.includes(type) ? Embed : BlockEmbed;
  class CustomBlock extends EmbedType {
    static blotName = type;
    static tagName = CUSTOM_INLINE.includes(type) ? "span" : "div";
    static className = `ql-${type}`;
    static refs = {};

    static create(value) {
      let node = super.create();
      const id = generateId("ED");
      const refs = this.refs;
      node.classList.add("custom-block");
      node.setAttribute("data-id", id);
      if (!CUSTOM_INLINE.includes(type))
        node.setAttribute("contenteditable", false);
      this.data = value;
      this.refs = {
        ...refs,
        [id]: React.createRef()
      };
      return node;
    }

    static value(domNode) {
      const id = domNode.getAttribute("data-id");
      const ref = this.refs[id];
      return ref && ref.current && ref.current.getData();
    }

    constructor(domNode) {
      super(domNode);
      this.id = domNode.getAttribute("data-id");
      this.data = this.statics.data;
      this.onDelete = this.onDelete.bind(this);
      this.component = blotComponent;
    }

    //TODO: Need to revisit this logic again
    update(mutations, context) {
      // `childList` mutations are not handled on Quill
      // see `update` implementation on:
      // https://github.com/quilljs/quill/blob/master/blots/embed.js
      if (![FORMAT_TYPES.MENTION, FORMAT_TYPES.HASHTAG].includes(type)) {
        super.update(mutations, context);
        return;
      }
      for (const mutation of mutations) {
        if (mutation.type !== "childList") continue;
        if (mutation.removedNodes.length === 0) continue;

        const nodeType = mutation.removedNodes[0].nodeType;
        setTimeout(() => this._remove(nodeType), 0);
        return;
      }

      const unhandledMutations = mutations.filter(
        (m) => m.type !== "childList"
      );
      super.update(unhandledMutations, context);
    }

    _remove(nodeType) {
      try {
        let adjust = 0;
        if (nodeType === Node.TEXT_NODE) adjust = -1;
        let quill = Quill.find(this.scroll.domNode.parentNode);
        let cursorPosition = null;
        if (quill?.getSelection?.()) {
          cursorPosition = quill.getSelection().index + adjust;
        }

        // see `remove` implementation on:
        // https://github.com/quilljs/parchment/blob/master/src/blot/abstract/shadow.ts
        this.remove();

        // schedule cursor positioning after quill is done with whatever has scheduled
        setTimeout(() => {
          if (cursorPosition !== null)
            quill.setSelection(cursorPosition, Quill.sources.API);
        }, 0);
      } catch (e) {
        console.info("error in custom block", e);
      }
    }

    attach() {
      super.attach();
      this.scroll && this.scroll.emitter.emit(Scroll.events.BLOT_MOUNT, this);
    }

    renderPortal(id, componentRenderer = null) {
      let options = {};
      if (this.scroll) {
        options = Quill.find(this.scroll.domNode.parentNode).options;
      }
      const ref = this.statics.refs[id];
      const key = `editor-${this.statics.blotName}-${id}`;
      if (componentRenderer) {
        this.component = componentRenderer;
      }
      const qlEmbedNode = CUSTOM_INLINE.includes(type)
        ? this.contentNode
        : this.domNode;
      return createPortal(
        <this.component
          type={this.statics.blotName}
          ref={ref}
          node={this.data}
          readOnly={options.readOnly}
          onDelete={this.onDelete}
          domNode={qlEmbedNode}
          flowId={flowId}
        />,
        qlEmbedNode,
        key
      );
    }

    detach() {
      super.detach();
      const ref = this.statics.refs[this.id];
      if (this.data && this.data.onDelete) {
        this.data.onDelete();
      }
      ref &&
        ref.current &&
        ref.current.onDelete &&
        ref.current.onDelete(this.data);
      this.scroll && this.scroll.emitter.emit(Scroll.events.BLOT_UNMOUNT, this);
      if (this.statics.refs[this.id])
        Reflect.deleteProperty(this.statics.refs, this.id);
      this.domNode?.remove && this.domNode.remove();
    }

    onDelete() {
      this.detach();
    }
  }
  return CustomBlock;
};
